import { ArcElement, Chart as ChartJS, Tooltip } from 'chart.js';
import { useTheme } from 'hephaestus/web';
import { Doughnut } from 'react-chartjs-2';
import { useChartColors } from './getChartColors';

/** DoughnutController is registered by `react-chartjs-2` on import; arc elements must be registered explicitly. */
ChartJS.register(ArcElement, Tooltip);

const isNegative = (value?: number) => value && value < 0;

export const PieChart = ({
  datasets,
  labels,
  onHover = () => {},
  tooltip = false,
  customColors,
  height,
  width,
  cutoutPercentage = 70,
  testId = 'pie-chart',
}: {
  datasets: any[];
  labels: string[];
  onHover?: (i: number) => void;
  tooltip?: boolean;
  customColors?: any[];
  height?: string;
  width?: string;
  cutoutPercentage?: number;
  testId?: string;
}) => {
  const theme = useTheme();
  const chartColors = useChartColors();
  const colors = customColors ?? chartColors;

  const options = {
    maintainAspectRatio: false,
    animation: {
      duration: 0,
    },
    cutout: `${cutoutPercentage}%`,
    plugins: {
      tooltip: {
        enabled: tooltip,
      },
      legend: {
        display: false,
      },
    },
    onHover: (_event: any, chartElement: any) =>
      onHover(chartElement.length > 0 ? chartElement[0].index : undefined),
  };

  const fullData = {
    labels,
    datasets: (datasets || []).map((x: any) => ({
      fill: true,
      label: x.label,
      data: x.data,
      backgroundColor: (x?.data ?? []).map((value: any, i: number) =>
        isNegative(value) ? theme.color.surface : colors[i % colors.length],
      ),
      borderWidth: (x?.data ?? []).map((value: any) =>
        isNegative(value) ? 1 : 0,
      ),
      borderColor: (x?.data ?? []).map(
        (_: any, i: number) => colors[i % colors.length],
      ),
      borderDash: [5, 5],
      hoverBackgroundColor: (x?.data ?? []).map((value: any, i: number) =>
        isNegative(value) ? theme.color.surface : colors[i % colors.length],
      ),
      hoverBorderColor: (x?.data ?? []).map(
        (_: any, i: number) => colors[i % colors.length],
      ),
    })),
  };

  const chartStyles = {
    height: height || '400px',
    width: width || '400px',
  };

  return (
    <Doughnut
      options={options}
      data={fullData}
      style={chartStyles}
      data-testid={testId}
    />
  );
};
